import React, { useEffect, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";

import { formatIndianCurrency } from "../../helper/ConvertValue";
import { updateConventionalMode } from "../../redux/actions/TemplateTabs/actionCM";

import commoncss from "../../myfin_v2/css/common.module.css";
import buttoncss from "../../myfin_v2/css/button.module.css";
import tablecss from "../../myfin_v2/css/table.module.css";
import ReactIcon from "../../myfin_v2/css/table.module.css";
import { ReactComponent as BackIcon } from "../../myfin_v2/assets/icons/left_arrow.svg";

import { ThemeContext } from "../../helper/DarkModeContext";
import { Button } from "../../myfin_v2/elements/Button";
/**
 * GroupingDrilldownTable
 *
 * Props:
 *  - grouping: array (grouping structure)
 *  - onDrop: function(firstDroppedItem, targetLevel3Row, updatedGrouping) -> void
 *
 * Behavior:
 *  - Accepts drops only on level-3 rows.
 *  - Reads dropped payload (tries application/json, text/plain, then global fallback).
 *  - Extracts cy/py (or currentYear/previousYear variants) and updates the specific level-3 row.
 *  - Calls onDrop(...) after updating local state so the parent can persist the change.
 */
const GroupingDrilldownTable = ({ grouping = [], onDrop }) => {
  const conventionModeData = useSelector(
    (state) => state.ConventionModeReducer
  );
  const dispatch = useDispatch();

  const { theme } = useContext(ThemeContext);

  // internal editable copy of grouping
  const [data, setData] = useState(grouping || []);

  useEffect(() => {
    setData(grouping || []);
  }, [grouping]);

  // parse payload helper
  const getParsedPayloadFromEvent = (e) => {
    try {
      const json = e.dataTransfer.getData("application/json");
      if (json) {
        try {
          return JSON.parse(json);
        } catch {
          return json;
        }
      }
    } catch (err) {
      // ignore
    }
    try {
      const txt = e.dataTransfer.getData("text/plain");
      if (txt) {
        try {
          return JSON.parse(txt);
        } catch {
          return txt;
        }
      }
    } catch (err) {
      // ignore
    }
    // fallback for your global approach (if used)
    if (typeof window !== "undefined" && window.__MY_APP_DRAG_PAYLOAD) {
      return window.__MY_APP_DRAG_PAYLOAD;
    }
    return null;
  };

  // flexible extractor for cy/py fields
  const extractCyPyFromItem = (item) => {
    if (!item) return { cy: null, py: null };
    if (typeof item.cy !== "undefined" || typeof item.py !== "undefined") {
      return { cy: item.cy ?? null, py: item.py ?? null };
    }
    if (
      typeof item.currentYear !== "undefined" ||
      typeof item.previousYear !== "undefined"
    ) {
      return { cy: item.currentYear ?? null, py: item.previousYear ?? null };
    }
    if (
      typeof item.current_year !== "undefined" ||
      typeof item.previous_year !== "undefined"
    ) {
      return { cy: item.current_year ?? null, py: item.previous_year ?? null };
    }
    return { cy: null, py: null };
  };

  // view state to switch between root and detail (drilldown)
  const [view, setView] = useState({
    type: "root",
    level1: null,
    level2: null,
  });
  const [hoveredL3Id, setHoveredL3Id] = useState(null);

  const handleNoteClick = (level1, level2) =>
    setView({ type: "detail", level1, level2 });
  const handleBackToRoot = () =>
    setView({ type: "root", level1: null, level2: null });

  // drop handlers
  const onLevel3DragOver = (e) => {
    e.preventDefault(); // necessary to allow drop
  };

  const onLevel3Drop = (l3Row) => async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const parsed = getParsedPayloadFromEvent(e);
    if (!parsed) {
      console.warn(
        "Drop received but no parsed payload found (dataTransfer empty)."
      );
      return;
    }

    // resolve the first item from common payload shapes
    let firstItem = null;
    if (Array.isArray(parsed)) firstItem = parsed[0];
    else if (parsed.items && Array.isArray(parsed.items))
      firstItem = parsed.items[0];
    else if (parsed.item) firstItem = parsed.item;
    else firstItem = parsed;

    const { cy, py } = extractCyPyFromItem(firstItem);
    if (cy == null && py == null) {
      console.warn("Dropped payload didn't include cy/py values to map.", {
        parsed,
        firstItem,
      });
      return;
    }

    // immutably update the specific level-3 row's currentYear and previousYear
    const updatedGrouping = data.map((l1) => {
      if (!Array.isArray(l1.subgroup)) return l1;
      return {
        ...l1,
        subgroup: l1.subgroup.map((l2) => {
          if (!Array.isArray(l2.subgroup)) return l2;
          return {
            ...l2,
            subgroup: l2.subgroup.map((l3) => {
              if (l3._id === l3Row._id) {
                const newL3 = {
                  ...l3,
                  currentYear: cy != null ? cy : l3.currentYear,
                  previousYear: py != null ? py : l3.previousYear,
                };
                console.log("Level-3 update:", {
                  before: l3,
                  after: newL3,
                  droppedPayload: firstItem,
                });
                const dragItem = firstItem?.item_;
                dragItem["map_data"] = {
                  cy_map: {
                    fs_grp: l1?.name,
                    note_grp: l2.name,
                    note_no: l2?.notes,
                    sub_grp: l3.name,
                  },
                  py_map: {
                    fs_grp: l1?.name,
                    note_grp: l2.name,
                    note_no: l2?.notes,
                    sub_grp: l3.name,
                  },
                };
                const updatePayload = dragItem;

                dispatch(
                  updateConventionalMode(
                    conventionModeData.set_payload,
                    updatePayload
                  )
                );

                return newL3;
              }
              return l3;
            }),
          };
        }),
      };
    });

    // update local state
    setData(updatedGrouping);

    // call parent callback so the parent can persist the change (redux/api)
    if (typeof onDrop === "function") {
      try {
        onDrop(firstItem, l3Row, updatedGrouping);
      } catch (err) {
        console.error("onDrop callback threw an error:", err);
      }
    }
  };

  const onLevel3DragEnter = (id) => (e) => {
    e.preventDefault();
    setHoveredL3Id(id);
  };
  const onLevel3DragLeave = (id) => (e) => {
    e.preventDefault();
    setHoveredL3Id((curr) => (curr === id ? null : curr));
  };

  // Helper to find freshLevel2 (so updated values are reflected when drilling down)
  const { level1, level2 } = view;
  const freshLevel2 =
    (level2 &&
      data
        .find((l1) => l1._id === level1?._id)
        ?.subgroup?.find((s) => s._id === level2?._id)) ||
    level2;

  // Render: Root view
  if (view.type === "root") {
    return (
      <div>
        <table
          className={tablecss.customTable}
          style={{ width: "100%", borderCollapse: "collapse" }}
        >
          <thead>
            <tr>
              <th className={`text-left pl-3 ${commoncss.highlightRow}`}>
                Particulars
              </th>
              <th className={`text-left pl-3 ${commoncss.highlightRow}`}>
                Note
              </th>
              <th className={`text-right pl-3 ${commoncss.highlightRow}`}>
                Current Year
              </th>
              <th className={`text-right pl-3 ${commoncss.highlightRow}`}>
                Previous Year
              </th>
            </tr>
          </thead>
          <tbody
            style={{
              background: `${theme ? "" : "#363535"}`,
              color: `${theme ? "" : "#fff"}`,
            }}
          >
            {data.map((l1) => (
              <React.Fragment key={l1._id}>
                <tr className={`text-left pl-3 ${commoncss.highlightRow}`}>
                  <td
                    colSpan={4}
                    className={`text-left pl-3 ${commoncss.highlightRow} ${commoncss.fs14} ${commoncss.fw600}`}
                    style={{
                      padding: "8px",
                      borderBottom: "0.5px solid transparent",
                    }}
                  >
                    {l1.name}
                  </td>
                </tr>

                {(l1.subgroup || []).map((l2) => (
                  <tr key={l2._id}>
                    <td
                      className="text-left"
                      style={{
                        borderBottom: "0.5px solid transparent",
                        padding: "8px",
                      }}
                    >
                      {l2.name}
                    </td>
                    <td
                      style={{
                        borderBottom: "0.5px solid transparent",
                        padding: "8px",
                        cursor: "pointer",
                        textDecoration: "underline",
                        textAlign: "center",
                      }}
                      onClick={() => handleNoteClick(l1, l2)}
                    >
                      {l2.notes ?? ""}
                    </td>
                    <td
                      className="text-right"
                      style={{
                        borderBottom: "0.5px solid transparent",
                        padding: "8px",
                      }}
                    >
                      {formatIndianCurrency(l2.currentYear ?? 0)}
                    </td>
                    <td
                      className="text-right"
                      style={{
                        borderBottom: "0.5px solid transparent",
                        padding: "8px",
                      }}
                    >
                      {formatIndianCurrency(l2.previousYear ?? 0)}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Render: Detail view (level2 + level3)
  return (
    <div>
      <Button
        btnColor={buttoncss.primary}
        label={"Back"}
        onClick={handleBackToRoot}
        // icon={<BackIcon />}
      />

      <table
        className={tablecss.customTable}
        style={{ width: "100%", borderCollapse: "collapse" }}
      >
        <thead>
          <tr>
            <th className={`text-left pl-3 ${commoncss.highlightRow}`}>
              Line item
            </th>
            <th className={`text-right pl-3 ${commoncss.highlightRow}`}>
              Current Year
            </th>
            <th className={`text-right pl-3 ${commoncss.highlightRow}`}>
              Previous Year
            </th>
          </tr>
        </thead>
        <tbody
          style={{
            background: `${theme ? "" : "#363535"}`,
            color: `${theme ? "" : "#fff"}`,
          }}
        >
          <tr className={`text-left pl-3 ${commoncss.highlightRow}`}>
            <td
              colSpan={3}
              className={`text-left pl-3 ${commoncss.highlightRow} ${commoncss.fs14} ${commoncss.fw600}`}
              style={{
                padding: "8px",
                borderBottom: "0.5px solid transparent",
              }}
            >
              {`${freshLevel2?.name ?? level2?.name}`}
            </td>
          </tr>

          {(freshLevel2?.subgroup || []).map((l3) => {
            const isHovered = hoveredL3Id === l3._id;
            return (
              <tr
                key={l3._id}
                onDragOver={onLevel3DragOver}
                onDrop={onLevel3Drop(l3)}
                onDragEnter={onLevel3DragEnter(l3._id)}
                onDragLeave={onLevel3DragLeave(l3._id)}
                style={{
                  backgroundColor: isHovered ? "#e6f7ff" : "transparent",
                }}
              >
                <td
                  className="text-left"
                  style={{
                    borderBottom: "0.5px solid transparent",
                    padding: "8px",
                  }}
                >
                  {l3.name}
                </td>
                <td
                  className="text-right"
                  style={{
                    borderBottom: "0.5px solid transparent",
                    padding: "8px",
                  }}
                >
                  {formatIndianCurrency(l3.currentYear ?? 0)}
                </td>
                <td
                  className="text-right"
                  style={{
                    borderBottom: "0.5px solid transparent",
                    padding: "8px",
                  }}
                >
                  {formatIndianCurrency(l3.previousYear ?? 0)}
                </td>
              </tr>
            );
          })}

          {!freshLevel2?.subgroup?.length && (
            <tr>
              <td
                colSpan={3}
                style={{
                  borderBottom: "0.5px solid transparent",
                  padding: "8px",
                }}
              >
                No Level 3 items.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default GroupingDrilldownTable;
